import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import axioInstance from "../../../../../../../../../custom_utilities/AxiosInstance/axiosInstance";
import { url_304 } from "../../../../../../../../../custom_utilities/api_services";
import learnIcon from "../../../../../../../../../utilities/images/package_course_modes/icon-learn.svg";
import practiceIcon from "../../../../../../../../../utilities/images/package_course_modes/icon-practice.svg";
import practiceActiveIcon from "../../../../../../../../../utilities/images/package_course_modes/icon-practice-active.svg";
import testIcon from "../../../../../../../../../utilities/images/package_course_modes/icon-test.svg";
import testActiveIcon from "../../../../../../../../../utilities/images/package_course_modes/icon-test-active.svg";
import "./StoreCourseMode.scss";

const MODES = [
  { id: "learn", label: "LEARN", icon: learnIcon, section: "7" },
  {
    id: "practice",
    label: "PRACTICE",
    icon: practiceIcon,
    activeIcon: practiceActiveIcon,
    section: "8",
  },
  { id: "test", label: "TEST", icon: testIcon, activeIcon: testActiveIcon, section: "9" },
];

const SECTION_MODE = { 7: "learn", 8: "practice", 9: "test" };

let cachedPlanVariants = null;

const readPlanVariants = (payload) => {
  const data = payload?.data?.data || payload?.data || payload;
  const variants =
    data?.bundle_plan?.build_plan?.variants ||
    data?.explore?.bundle_plan?.build_plan?.variants ||
    data?.build_plan?.variants ||
    [];
  return Array.isArray(variants) ? variants : [];
};

const loadPlanVariants = async () => {
  if (cachedPlanVariants) return cachedPlanVariants;
  const response = await axioInstance.get(url_304);
  cachedPlanVariants = readPlanVariants(response?.data);
  return cachedPlanVariants;
};

const modeFromName = (name = "") => {
  const text = String(name).toLowerCase();
  if (/\(l\+p\+t\)|\bmastery\b/.test(text)) return "learn";
  if (/\bpractice\b|\(p\)/.test(text) && !/\blearn\b|\(l\)/.test(text)) return "practice";
  if (/\btest\b|\(t\)/.test(text) && !/\blearn\b|\bpractice\b/.test(text)) return "test";
  if (/\blearn\b|\(l\)/.test(text)) return "learn";
  return "";
};

const modeFromVariant = (variant) => {
  const ids = String(variant?.section_ids || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (ids.length !== 1) return "";
  return SECTION_MODE[ids[0]] || "";
};

const closestVariant = (variants, section, duration) => {
  const pool = variants.filter((variant) => String(variant.section_ids) === section);
  if (!pool.length) return null;
  if (!Number.isFinite(duration)) {
    return pool.find((variant) => Number(variant.duration) === 12) || pool[0];
  }
  return pool
    .slice()
    .sort(
      (left, right) =>
        Math.abs(Number(left.duration) - duration) - Math.abs(Number(right.duration) - duration)
    )[0];
};

const StoreCourseMode = ({ packageData }) => {
  const history = useHistory();
  const [planVariants, setPlanVariants] = useState(cachedPlanVariants || []);
  const currentName = packageData?.PackageInstallmentDetails?.package_name || "";
  const currentId = packageData?.PackageInstallmentDetails?.package_id;
  const currentVariant = planVariants.find(
    (variant) => String(variant.package_id) === String(currentId)
  );
  const activeMode = modeFromVariant(currentVariant) || modeFromName(currentName) || "learn";

  useEffect(() => {
    let cancelled = false;
    loadPlanVariants()
      .then((variants) => {
        if (!cancelled) setPlanVariants(variants);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const openMode = (modeId) => {
    if (modeId === activeMode || !currentVariant) return;
    const mode = MODES.find((item) => item.id === modeId);
    const target = closestVariant(planVariants, mode?.section, Number(currentVariant.duration));
    const nextId = target?.package_id;
    if (nextId && String(nextId) !== String(currentId)) {
      history.push(`/package/${nextId}`);
    }
  };

  return (
    <div className="course-mode" role="tablist" aria-label="Course mode">
      {MODES.map((mode) => (
        <button
          key={mode.id}
          type="button"
          role="tab"
          aria-selected={activeMode === mode.id}
          className={`course-mode__button is-${mode.id}${
            activeMode === mode.id ? " is-active" : ""
          }`}
          onClick={() => openMode(mode.id)}
        >
          <span className="course-mode__icon">
            <img src={activeMode === mode.id && mode.activeIcon ? mode.activeIcon : mode.icon} alt="" />
          </span>
          {mode.label}
        </button>
      ))}
    </div>
  );
};

export default StoreCourseMode;
